import React, { useEffect, useState } from "react";
import { message } from "antd";
import Service from "../../service";
import { showAuthLoader, hideAuthLoader } from "../../appRedux/actions/Auth";
import { useDispatch } from "react-redux";
import moment from "moment";
import { GLOBAL_CONFIG } from "../../constants/permission_id";
import DynamicFormRenderer from "../FormBuilder/DynamicFormRenderer";
import {
  PREDEFINED_SECTION,
  PREDEFINED_FIELDS,
} from "../../constants/exitFeedbackPredefinedFields";
import "./FeedbackForm.css";
import useCompanyDateFormat from "../../hooks/useCompanyDateFormat";

const API_URL = `${process.env.REACT_APP_API_URL}/uploads/company_image`;

/** Options are stored as {key, value}; DynamicFormRenderer expects {value, label}. */
const toRendererOptions = (options) =>
  (options || []).map((opt) => ({
    value: opt.value !== undefined ? opt.value : opt,
    label: opt.key !== undefined ? opt.key : opt.value !== undefined ? opt.value : opt,
  }));

/** Maps a predefined/custom field (fieldName/fieldLabel shape) into the {name,label,...} shape DynamicFormRenderer renders. */
const toRendererField = (field) => ({
  name: field.fieldName,
  fieldName: field.fieldName,
  label: field.fieldLabel,
  fieldLabel: field.fieldLabel,
  fieldType: field.fieldType,
  required: field.required || false,
  placeholder: field.placeholder,
  options: toRendererOptions(field.options),
  order: field.order ?? 0,
});

/**
 * Merges the predefined (toggle-only) fields - filtered/overridden by the company's
 * predefinedConfig - with the admin-built custom sections/fields from the API into
 * the { sections: [{ sectionName, sectionKey, fields }] } + flat `fields` shape
 * DynamicFormRenderer expects (sections drive rendering, flat fields drive its
 * internal handleSubmit payload building).
 */
const buildFormConfig = ({ predefinedConfig, sections, fields }) => {
  const fieldsActive = predefinedConfig?.predefinedFieldsActive || {};
  const fieldsRequired = predefinedConfig?.predefinedFieldsRequired || {};
  const predefinedSectionActive = predefinedConfig?.predefinedSectionActive !== false;

  const activePredefinedFields = [...PREDEFINED_FIELDS]
    .filter((field) => fieldsActive[field.fieldName] !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((field) =>
      toRendererField({
        ...field,
        required:
          fieldsRequired[field.fieldName] !== undefined
            ? fieldsRequired[field.fieldName]
            : field.required,
      })
    );

  const builtSections = [];

  if (predefinedSectionActive && activePredefinedFields.length > 0) {
    builtSections.push({
      sectionName: PREDEFINED_SECTION.sectionName,
      sectionKey: PREDEFINED_SECTION.sectionKey,
      fields: activePredefinedFields,
    });
  }

  const customSections = [...(sections || [])].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );
  customSections.forEach((section) => {
    const sectionFields = (fields || [])
      .filter((field) => field.sectionKey === section.sectionKey)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map(toRendererField);

    if (sectionFields.length > 0) {
      builtSections.push({
        sectionName: section.sectionName,
        sectionKey: section.sectionKey,
        fields: sectionFields,
      });
    }
  });

  const flatFields = builtSections.flatMap((section) => section.fields);

  return { sections: builtSections, fields: flatFields };
};

const FeedbackForm = (props) => {
  const dateFormat = useCompanyDateFormat();
  const params_id = props.match?.params?.id || props.computedMatch?.params?.id;
  const [empApiData, setEmpApiData] = useState(null);
  const [formConfig, setFormConfig] = useState(null);
  // This is a public, unauthenticated link (no prior login in this browser
  // to have populated localStorage), so the company logo has to come from
  // the API response itself rather than the LogoUrl_<slug> localStorage key
  // every other (authenticated-session) page relies on.
  const [companyLogoUrl, setCompanyLogoUrl] = useState("");
  const dispatch = useDispatch();
  const [responseData, setResponseData] = useState({});

  useEffect(() => {
    getEmployeeById();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getEmployeeById = async () => {
    try {
      dispatch(showAuthLoader());
      const params = `/${params_id}`;
      const response = await Service.makeAPICall({
        methodName: Service.getMethod,
        api_url: Service.feedbackApi + params,
      });

      if (response.data && response.data.data) {
        const { employee, predefinedConfig, sections, fields, companyLogoUrl: logoUrl } = response.data.data;
        setEmpApiData(employee);
        setCompanyLogoUrl(logoUrl || "");
        setFormConfig(buildFormConfig({ predefinedConfig, sections, fields }));
        dispatch(hideAuthLoader());
      } else if (response.data) {
        setResponseData(response.data);
        if (response.data.status !== 403) {
          message.error(response.data.message);
        }
        dispatch(hideAuthLoader());
      }
    } catch (error) {
      dispatch(hideAuthLoader());
      console.log(error);
    }
  };

  const handleSubmit = async (payload) => {
    try {
      dispatch(showAuthLoader());

      const dynamicFields = payload?.dynamicFields || [];
      const answers = {};
      const formData = new FormData();
      formData.append("email_token_id", `${params_id}`);

      const answeredFieldNames = new Set();
      dynamicFields.forEach((field) => {
        answeredFieldNames.add(field.fieldName);
        if (field.fieldType === "file") {
          if (field.value instanceof File) {
            formData.append(`file_${field.fieldName}`, field.value);
          } else if (Array.isArray(field.value)) {
            field.value.forEach((file) => {
              if (file instanceof File) {
                formData.append(`file_${field.fieldName}`, file);
              }
            });
          }
        } else {
          answers[field.fieldName] = field.value;
        }
      });

      // DynamicFormRenderer's shared submit logic omits blank optional
      // fields from `dynamicFields` entirely (see its handleSubmit). Without
      // this, an unanswered field would be indistinguishable later from a
      // field that didn't exist yet when this was submitted. Backfilling
      // every field from `formConfig` (the exact snapshot presented to this
      // employee) with "" makes the stored answers a permanent, complete
      // record as of submission time - the admin view then just renders
      // whatever's stored, with no need to cross-reference the company's
      // current (possibly since-changed) field configuration.
      (formConfig?.fields || []).forEach((field) => {
        if (!answeredFieldNames.has(field.fieldName)) {
          answers[field.fieldName] = "";
        }
      });

      formData.append("answers", JSON.stringify(answers));

      const response = await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: Service.postFeedbackData,
        body: formData,
      });

      if (response.data && response.data.status == 200) {
        dispatch(hideAuthLoader());
        message.success(response.data.message);
        setTimeout(() => {
          window.close();
        }, 2000);
      } else if (response.data) {
        setResponseData(response.data);
        if (response.data.status !== 403) {
          message.error(response.data.message);
        }
        dispatch(hideAuthLoader());
      }
    } catch (error) {
      dispatch(hideAuthLoader());
      console.log(error);
      message.error("Failed to submit form");
    }
  };

  return (
    <div className="exit-feedback-page">
      <div className="exit-feedback-header">
        <a href="/">
          {companyLogoUrl ? (
            <img
              alt="Logo"
              src={`${API_URL}/${companyLogoUrl}`}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = GLOBAL_CONFIG.LOGO_WHITE;
              }}
            />
          ) : (
            <img alt="Logo" src={GLOBAL_CONFIG.LOGO_WHITE} />
          )}
        </a>
      </div>
      <div className="exit-feedback-content">
        {responseData.status !== 403 && (
          <>
            <div className="exit-feedback-title-card">
              <h2>Exit Feedback Form</h2>
              <div className="exit-feedback-summary">
                <span><b>Name:</b> {empApiData ? empApiData.full_name : "-"}</span>
                <span>
                  <b>Date:</b>{" "}
                  {empApiData?.emp_last_working_date
                    ? moment(empApiData.emp_last_working_date).format(dateFormat)
                    : "-"}
                </span>
              </div>
            </div>
            {formConfig && (
              <DynamicFormRenderer
                formConfig={formConfig}
                onSubmit={handleSubmit}
                submitButtonText="Save"
                showResetButton={false}
              />
            )}
          </>
        )}
        {responseData.status === 403 && (
          <div className="exit-feedback-response-msg">
            <h2>{responseData.message}</h2>
          </div>
        )}
      </div>
    </div>
  );
};
export default FeedbackForm;
